import {
  readMoneyV2,
  readSignedMoneyV2,
  readTotalsMoneyV2,
  type MoneyGroupV2,
  type MoneyV2,
  type SignedMoneyV2,
  type TotalsMoneyV2,
} from "@voltie/localization-web";
import type { InvalidRead, OrgSessionsReportErrorCode } from "@/lib/orgSessionsReport";

// The money of one F8 report totals object (CUR-404 F8 S3a): the shared money_v2
// (totals_money_v2) and the report summary summary_money_v2 (report_summary_money_v2).
// money_v2 is read by readTotalsMoneyV2; the summary is a closed block read strictly here.
// Each summary entry is one currency's accepted rows and is paired only with the shared
// finalized billed group of that currency, a lookup. No amount is added, converted or
// defaulted and no currency is combined; the only arithmetic is the population count
// excluded_rows = total_sessions - sum(entry.rows). Rule names follow S3a
// report_summary_errors / report_totals_errors. Not checked here, by design: the MV-2
// registry exponent (no client registry) and MV-7 (the legacy totals keys are dropped
// unread), both the server's checks.

export const REPORT_SUMMARY_MEMBERS = [
  "billed_estimate",
  "revenue_captured",
  "processing_fee",
  "voltie_fee",
  "payout_to_organization",
  "organization_owes_voltie",
  "organization_net",
] as const;

type ReportSummaryMemberName = (typeof REPORT_SUMMARY_MEMBERS)[number];

/** One summary member: amount null exactly when rows === 0 && unknown_rows > 0 (unknown, never 0). */
export interface ReportSummaryMember<M extends MoneyV2 | SignedMoneyV2 = MoneyV2> {
  readonly amount: M | null;
  readonly rows: number;
  readonly unknown_rows: number;
  readonly complete: boolean;
}

/** One currency's accepted rows; never a cross-currency entry. */
export interface ReportSummaryEntry {
  readonly currency: string;
  readonly exponent: number;
  readonly rows: number;
  readonly billed_estimate: ReportSummaryMember;
  readonly revenue_captured: ReportSummaryMember;
  readonly processing_fee: ReportSummaryMember;
  readonly voltie_fee: ReportSummaryMember;
  readonly payout_to_organization: ReportSummaryMember;
  readonly organization_owes_voltie: ReportSummaryMember;
  /** Validated (net split), not displayed. */
  readonly organization_net: ReportSummaryMember<SignedMoneyV2>;
}

/** One currency line: the summary entry and the shared finalized-billed group of that currency (a lookup, no arithmetic). */
export interface ReportCurrencyTotals {
  readonly summary: ReportSummaryEntry;
  readonly finalized_billed: MoneyGroupV2 | null;
}

export type OrgReportTotalsMoney =
  | {
      readonly status: "value";
      /** readTotalsMoneyV2 result, server order, groups never added together. */
      readonly shared: TotalsMoneyV2;
      /** Ascending ISO order, as served. */
      readonly currencies: readonly ReportCurrencyTotals[];
      /** total_sessions - sum(entry.rows): rows in no group and no entry. */
      readonly excluded_rows: number;
    }
  | { readonly status: "none" }
  | InvalidRead;

export type ReportTotalsPath = "totals" | "period_totals";

const ENTRY_HEADER = ["currency", "exponent", "money_version", "rows"] as const;
const ENTRY_KEYS: readonly string[] = [...ENTRY_HEADER, ...REPORT_SUMMARY_MEMBERS];
const MEMBER_KEYS: readonly string[] = ["amount", "rows", "unknown_rows", "complete"];
const NET_SPLIT_HALVES = ["payout_to_organization", "organization_owes_voltie"] as const;

class Invalid extends Error {
  readonly error: OrgSessionsReportErrorCode;
  readonly path: string;
  readonly detail: string;

  constructor(error: OrgSessionsReportErrorCode, path: string, detail: string) {
    super(`${path}: ${error}: ${detail}`);
    this.error = error;
    this.path = path;
    this.detail = detail;
  }
}

const show = (value: unknown) => (typeof value === "string" ? JSON.stringify(value) : String(value));

/** A plain JSON object with exactly `keys`: a missing key first, then a key outside them. */
function closed(value: unknown, keys: readonly string[], path: string): Record<string, unknown> {
  const prototype = typeof value === "object" && value !== null && !Array.isArray(value) ? Object.getPrototypeOf(value) : undefined;
  if (prototype !== Object.prototype && prototype !== null) throw new Invalid("not_object", path, show(value));
  const record = value as Record<string, unknown>;
  const missing = keys.find((key) => !Object.hasOwn(record, key));
  if (missing !== undefined) throw new Invalid("missing_field", `${path}.${missing}`, missing);
  const extra = Object.keys(record).find((key) => !keys.includes(key));
  if (extra !== undefined) throw new Invalid("forbidden_key", `${path}.${extra}`, "not part of report_summary_money_v2");
  return record;
}

function count(value: unknown, min: number, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < min) throw new Invalid("counter", path, show(value));
  return value;
}

function member(input: unknown, name: ReportSummaryMemberName, entry: { currency: string; exponent: number; rows: number }, path: string): ReportSummaryMember<MoneyV2 | SignedMoneyV2> {
  const at = `${path}.${name}`;
  const record = closed(input, MEMBER_KEYS, at);
  const rows = count(record.rows, 0, `${at}.rows`);
  const unknown_rows = count(record.unknown_rows, 0, `${at}.unknown_rows`);
  if (typeof record.complete !== "boolean") throw new Invalid("member_complete", `${at}.complete`, show(record.complete));
  const complete = record.complete;

  // A negative fee is an amount error: only organization_net is signed.
  const read = name === "organization_net" ? readSignedMoneyV2(record.amount) : readMoneyV2(record.amount);
  if (read.status === "invalid") throw new Invalid(read.error, `${at}.amount`, read.detail);
  const amount = read.status === "value" ? read.value : null;
  if (amount !== null) {
    if (amount.currency !== entry.currency) throw new Invalid("currency_mismatch", `${at}.amount.currency`, `${amount.currency} in a ${entry.currency} entry`);
    if (amount.exponent !== entry.exponent) throw new Invalid("exponent_mismatch", `${at}.amount.exponent`, `${amount.exponent} in an entry with exponent ${entry.exponent}`);
  }

  // billed_estimate applies only to the entry's estimate-basis rows; every other member to all of them.
  const applicable = rows + unknown_rows;
  if (name === "billed_estimate" ? applicable > entry.rows : applicable !== entry.rows) {
    throw new Invalid("member_counts", at, `rows ${rows} + unknown_rows ${unknown_rows} beside entry rows ${entry.rows}`);
  }
  if (complete !== (unknown_rows === 0)) throw new Invalid("member_complete", `${at}.complete`, `complete ${complete} with unknown_rows ${unknown_rows}`);
  const unknown = rows === 0 && unknown_rows > 0;
  if (unknown ? amount !== null : amount === null) {
    throw new Invalid("member_amount", `${at}.amount`, unknown ? "known with no known row" : "unknown beside known or no applicable rows");
  }
  if (rows === 0 && unknown_rows === 0 && amount !== null && amount.amount_minor !== "0") throw new Invalid("member_amount", `${at}.amount`, `${amount.amount_minor} with no applicable row`);
  return Object.freeze({ amount, rows, unknown_rows, complete });
}

function entry(input: unknown, previous: string | undefined, path: string): ReportSummaryEntry {
  const record = closed(input, ENTRY_KEYS, path);
  if (record.money_version !== 2) throw new Invalid("money_version", `${path}.money_version`, show(record.money_version));
  const rows = count(record.rows, 1, `${path}.rows`);
  // The entry currency and exponent are valid exactly when a zero amount in them is.
  const probe = readMoneyV2({ currency: record.currency, amount_minor: "0", exponent: record.exponent, money_version: 2 });
  if (probe.status !== "value") {
    const key = probe.status === "invalid" && probe.error === "exponent" ? "exponent" : "currency";
    throw new Invalid(probe.status === "invalid" ? probe.error : "currency", `${path}.${key}`, show(record[key]));
  }
  const head = { currency: probe.value.currency, exponent: probe.value.exponent, rows };
  // Strictly ascending ISO codes, so each currency once.
  if (previous !== undefined && !(previous < head.currency)) throw new Invalid("currency_order", `${path}.currency`, `${head.currency} after ${previous}`);

  const members = Object.fromEntries(REPORT_SUMMARY_MEMBERS.map((name) => [name, member(record[name], name, head, path)])) as Record<ReportSummaryMemberName, ReportSummaryMember<MoneyV2 | SignedMoneyV2>>;
  const net = members.organization_net;
  for (const half of NET_SPLIT_HALVES) {
    const m = members[half];
    if (m.rows !== net.rows || m.unknown_rows !== net.unknown_rows) {
      throw new Invalid("net_split", `${path}.${half}`, `rows ${m.rows}/${m.unknown_rows} beside organization_net ${net.rows}/${net.unknown_rows}`);
    }
  }
  const payout = members.payout_to_organization.amount;
  const owes = members.organization_owes_voltie.amount;
  if (net.amount !== null && payout !== null && owes !== null && BigInt(payout.amount_minor) - BigInt(owes.amount_minor) !== BigInt(net.amount.amount_minor)) {
    throw new Invalid("net_split", `${path}.organization_net.amount`, `${payout.amount_minor} - ${owes.amount_minor} is not ${net.amount.amount_minor}`);
  }
  return Object.freeze({ currency: head.currency, exponent: head.exponent, rows, ...members }) as ReportSummaryEntry;
}

function summary(input: unknown, path: string): readonly ReportSummaryEntry[] {
  if (input === undefined || input === null) throw new Invalid("missing_field", path, "summary_money_v2");
  const record = closed(input, ["currencies"], path);
  const raw = record.currencies;
  if (!Array.isArray(raw)) throw new Invalid("not_array", `${path}.currencies`, show(raw));
  const entries: ReportSummaryEntry[] = [];
  for (const [i, item] of raw.entries()) entries.push(entry(item, entries.at(-1)?.currency, `${path}.currencies[${i}]`));
  return Object.freeze(entries);
}

function attempt<T>(read: () => T): T | InvalidRead {
  try {
    return read();
  } catch (e) {
    if (e instanceof Invalid) return Object.freeze({ status: "invalid", error: e.error, path: e.path, detail: e.detail });
    throw e;
  }
}

/** Reads a standalone summary_money_v2 (rules 3-13): closed shapes, ascending currencies and each entry's own rules. */
export function readReportSummaryMoneyV2(input: unknown, path: string): { readonly status: "value"; readonly value: readonly ReportSummaryEntry[] } | InvalidRead {
  return attempt(() => Object.freeze({ status: "value" as const, value: summary(input, path) }));
}

/** Reads the money of one admin totals object: money_v2, total_sessions, summary_money_v2, then the whole-object rules; the first failure wins. */
export function readReportTotalsMoney(totals: Record<string, unknown>, path: ReportTotalsPath): OrgReportTotalsMoney {
  return attempt(() => {
    const at = `${path}.money_v2`;
    const read = readTotalsMoneyV2(totals.money_v2);
    if (read.status === "unavailable") throw new Invalid("missing_field", at, "money_v2");
    if (read.status === "invalid") throw new Invalid(read.error, read.path ? `${at}.${read.path}` : at, read.detail);
    const shared = read.value;
    const total = count(totals.total_sessions, 0, `${path}.total_sessions`);
    const entries = summary(totals.summary_money_v2, `${path}.summary_money_v2`);

    // Rows in no entry are exactly the excluded rows: at least the always-excluded
    // counters, at most those plus every unresolved row, which may instead be an accepted partial row.
    const excluded_rows = total - entries.reduce((sum, e) => sum + e.rows, 0);
    const c = shared.completeness;
    const floor = c.unknown_currency_rows + c.conflict_rows + c.invalid_rows;
    if (excluded_rows < floor || excluded_rows > floor + c.unresolved_billing_rows) {
      throw new Invalid("population_identity", `${path}.total_sessions`, `${excluded_rows} rows in no entry, outside ${floor}..${floor + c.unresolved_billing_rows}`);
    }
    const byCurrency = new Map(entries.map((e) => [e.currency, e]));
    for (const [i, g] of shared.groups.entries()) {
      const owner = byCurrency.get(g.money.currency);
      if (owner === undefined) throw new Invalid("group_without_entry", `${at}.groups[${i}]`, `${g.basis} ${g.money.currency} has no summary entry`);
      if (g.rows > owner.rows) throw new Invalid("group_rows", `${at}.groups[${i}].rows`, `${g.rows} above entry rows ${owner.rows}`);
    }

    const currencies = entries.map((e) =>
      Object.freeze({ summary: e, finalized_billed: shared.groups.find((g) => g.basis === "billed" && g.money.currency === e.currency) ?? null }),
    );
    return Object.freeze({ status: "value" as const, shared, currencies: Object.freeze(currencies), excluded_rows });
  });
}
